import { FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { cn } from "utils";
import log from "logbeacon";
import { useQueryClient } from "@tanstack/react-query";
import { deviceDetector } from "@ospweb/sdk-ui";
import PaymentAlert, { AlertType } from "./PaymentAlert";
import { formatRemainDay } from "./utils/formatRemainDay";
import { SubscriptionPlanCard } from "./SubscriptionPlanCard";
import { getDisplayPlanName } from "../shared/planDisplayUtils";
import Translations from "@translate/components/Member/CurrentPlan/index";
import { QUERY_KEYS } from "@/src/config";
import { MemberKeys } from "@/src/screens/Member";
import { I18nUtil } from "@utils/i18n";
import {
  closePaymentError as closePaymentErrorUtil,
  createPaymentErrorFetcher,
  createPaymentErrorListener,
} from "@/src/utils/paymentErrorUtils";
import { useGetListPayment } from "@/src/hooks/useGetListPayment";
import { MemberPlanInfo } from "@/src/hooks";
import { useAppPayment } from "@/src/lib/bridge/useAppPayment";
import { useBridge } from "@/src/lib/bridge/useBridge";
import { MessageType } from "@/src/lib/bridge/types";
import { PayChannel } from "@/src/features/api/memberApi";
import { useIsPc } from "@/src/hooks/useIsPc";
import { ManageBillingUpgrade } from "./ManageBillingUpgrade";
import { GiftCodeSection } from "./GiftCodeSection";
import {
  isSubscriptionExpiring,
  SubscriptionExpiringActions,
} from "./SubscriptionExpiringNotice";

const I18n = I18nUtil.createScoped(Translations);

interface CurrentPlanPropsType {
  handleChange: (changeKey: string) => void;
  className?: string;
  data: MemberPlanInfo | undefined;
  isLoading: boolean;
}

/**
 * 用户当前计划组件，展示计划信息、使用统计和奖励
 * 内部处理数据获取和状态管理
 */
const CurrentPlan: FC<CurrentPlanPropsType> = (props: CurrentPlanPropsType) => {
  const { handleChange, className, data, isLoading } = props;
  const [paymentErrorId, setPaymentErrorId] = useState("");
  const [paymentManageUrl, setPaymentManageUrl] = useState("");
  const [paymentChannel, setPaymentChannel] = useState("");
  const { getListPayment } = useGetListPayment();
  const queryClient = useQueryClient();
  const { openSubscriptionManagement } = useAppPayment();
  const { send } = useBridge();
  const isInApp = deviceDetector.isApp();
  const isIos = deviceDetector.isIOSApp();
  const isAndroid = deviceDetector.isAndroidApp();
  const isFree = data?.isFree ?? true;
  const isExpiring = isSubscriptionExpiring(data);
  const remainDay = formatRemainDay(data?.remain_minutes);
  const [isPc] = useIsPc();
  const openExternalUrl = useCallback(
    (url?: string) => {
      if (!url) {
        return;
      }
      if (isInApp) {
        send(MessageType.OPEN_URL_EXTERNAL, { url });
        return;
      }
      window.open(url, "_blank");
    },
    [isInApp, send],
  );

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MEMBER_USE_PLAN] });
  }, [queryClient]);

  // 使用工具函数创建支付错误状态获取器
  const fetchPaymentErrorStatus = useMemo(() => {
    const wrappedGetListPayment = async (params: any) => {
      const result = await getListPayment(params);
      return result || [];
    };
    return createPaymentErrorFetcher(wrappedGetListPayment, {
      isLoggedIn: true, // 这里可以根据实际登录状态调整
      onPaymentErrorChange: (paymentErrorId, paymentUrl, payChannel) => {
        setPaymentErrorId(paymentErrorId);
        setPaymentManageUrl(paymentUrl);
        setPaymentChannel(payChannel);
      },
    });
  }, [getListPayment]);

  // 初始化时获取支付错误状态
  useEffect(() => {
    fetchPaymentErrorStatus();
  }, [fetchPaymentErrorStatus]);

  // 监听pubsub事件
  useEffect(() => {
    const unsubscribe = createPaymentErrorListener(
      fetchPaymentErrorStatus,
      true,
    );
    return unsubscribe;
  }, [fetchPaymentErrorStatus]);

  // 关闭支付错误提示
  const closePaymentError = useCallback(() => {
    closePaymentErrorUtil(paymentErrorId, undefined, setPaymentErrorId);
  }, [paymentErrorId]);

  // 处理"查看详情"点击
  const handleViewDetails = useCallback(() => {
    log.info("[Member][trace] View payment failure details");
    // 在实际项目中可能打开详情模态框或导航到详情页面
    // 打开PayPal付款记录页面
    if (isInApp) {
      if (paymentManageUrl) {
        openExternalUrl(paymentManageUrl);
      } else {
        openSubscriptionManagement();
      }
    } else {
      openExternalUrl(paymentManageUrl);
    }
  }, [paymentManageUrl, isInApp, openSubscriptionManagement, openExternalUrl]);

  const toMemberPrice = useCallback(() => {
    handleChange(MemberKeys.PRICING);
  }, [handleChange]);

  // 渲染加载状态
  if (isLoading) {
    return (
      <div
        className={cn(
          "bg-background_solid_secondary md:p-8 px-4 pt-4 flex flex-col",
          className,
        )}
      >
        <div className="animate-pulse self-stretch flex flex-col gap-6 max-w-4xl mx-auto w-full p-6 border border-border_white_transparent10 rounded-4">
          <div className="h-10 bg-background_white_overlay5 rounded-md w-1/4"></div>
          <div className="h-28 bg-background_white_overlay5 rounded-md w-full"></div>
          <div className="border-b border-border_white_transparent10"></div>
          <div className="h-40 bg-background_white_overlay5 rounded-md w-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col overflow-auto no-scrollbar h-full justify-between",
        className,
      )}
    >
      <div className="md:p-8 px-4 flex flex-col">
        <div className="self-stretch flex flex-col items-center gap-6 max-w-4xl mx-auto w-full pt-4 pb-24">
          {isPc && (
            <h1 className="w-full max-w-[740px] headline_regular_l text-content_primary">
              {I18n.t("Subscription")}
            </h1>
          )}
          {isPc && isExpiring && (
            <PaymentAlert
              onViewDetails={handleViewDetails}
              toMemberPrice={toMemberPrice}
              alertType={AlertType.SUBSCRIPTION_EXPIRING}
              remainDay={remainDay}
              name={getDisplayPlanName(data?.level)}
            />
          )}
          {paymentErrorId && (
            <PaymentAlert
              isShowViewDetails={
                (paymentChannel === "STRIPE" && !!paymentManageUrl) ||
                (paymentChannel === PayChannel.GOOGLE_IAP && isAndroid) ||
                (paymentChannel === PayChannel.APPLE_IAP && isIos)
              }
              onViewDetails={handleViewDetails}
              toMemberPrice={toMemberPrice}
              alertType={AlertType.PAYMENT_FAILED}
              closePaymentError={closePaymentError}
            />
          )}
          <SubscriptionPlanCard data={data} toMemberPrice={toMemberPrice} />
        </div>
      </div>
      <div className="flex-col flex mb-4 mx-4">
        {!isPc && (
          <>
            {true && (
              <SubscriptionExpiringActions
                data={data}
                toMemberPrice={toMemberPrice}
              />
            )}
            <ManageBillingUpgrade
              isFree={isFree}
              toMemberPrice={toMemberPrice}
              data={data}
            />
          </>
        )}
        <GiftCodeSection />
      </div>
    </div>
  );
};

export default memo(CurrentPlan);
